import {
    Button,
    Col,
    Flex,
    Form,
    Row,
    Switch,
    Typography,
    Card,
    notification,
    theme,
    Divider,
} from 'antd';
import React, { useEffect, useState } from 'react';
import UserProfileAPI from '../../../restAPI/UserProfileAPI';
import { useSelector } from 'react-redux';
import CommonSkeleton from '../../../helperComponents/CommonSkeleton'
const { Title, Text } = Typography;

export default function TwoFactorAuth() {
    const {
        token: { colorBgContainer, borderRadiusLG, colorPrimary, colorBorder },
    } = theme.useToken();

    const { companyId, token, userCompany } = useSelector(state => state.auth);
    const [enable2FA, setEnable2FA] = useState(false);
    const [isSms2faEnabled, setIsSms2faEnabled] = useState(false);
    const [isEmail2faEnabled, setIsEmail2faEnabled] = useState(false);
    const [isDisabledSMS, setIsDisabledSMS] = useState(false);
    const [mobile, setmobile] = useState('');
    const [email, setEmail] = useState('');
    const [maskedMobile, setMaskedMobile] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [is2FALoading, setIs2FALoading] = useState(false);
        
    const [originalSms2faEnabled, setOriginalSms2faEnabled] = useState(false);
    const [originalEmail2faEnabled, setOriginalEmail2faEnabled] = useState(false);
    const [originalEnable2FA, setOriginalEnable2FA] = useState(false);

    const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };

    const getTwoFactorAuthenticationData = React.useCallback(async () => {
        try{
            setIs2FALoading(true);
            const {data} = await UserProfileAPI.getTwoFactorStatus(companyId,token);            
            const {isEmail2faEnabled , isSms2faEnabled , options} = data;
            if(isEmail2faEnabled || isSms2faEnabled){
                setEnable2FA(true);
            }else if(!isEmail2faEnabled && !isSms2faEnabled){
                setEnable2FA(false);
            }
            setIsSms2faEnabled(isSms2faEnabled);
            setIsEmail2faEnabled(isEmail2faEnabled);
            
            const originalEnable2FAValue = isEmail2faEnabled || isSms2faEnabled;
            setOriginalSms2faEnabled(isSms2faEnabled);
            setOriginalEmail2faEnabled(isEmail2faEnabled);
            setOriginalEnable2FA(originalEnable2FAValue);
            if (options.phone.length === 0) {
                setIsDisabledSMS(true);
            }
            setmobile(options.phone);
            setEmail(options.email);
            const phone = options.phone[0];
            const masked = phone && phone.length > 4
                    ? phone.slice(0, -4).replace(/./g, 'x') + phone.slice(-4)
                    : phone;
            setMaskedMobile(masked);

            // Mask email: use***@example.com
            const email = options.email;
            const atIdx = email.indexOf('@');
            let maskedEmail = email;
            if (atIdx > 3) {
                maskedEmail = email.slice(0, 3) +
                    '*'.repeat(atIdx - 3) +
                    email.slice(atIdx);
            }
            setMaskedEmail(maskedEmail);
        }catch(error){
            console.log("error getting email :", error.message);
        }finally{
            setIs2FALoading(false);
        }
    }, [companyId, token]);
    
    
    const hasChanges = React.useMemo(() => {
        const currentEnable2FA = isSms2faEnabled || isEmail2faEnabled;    
        const switchesChanged = isSms2faEnabled !== originalSms2faEnabled || 
                               isEmail2faEnabled !== originalEmail2faEnabled;        
        const mainStateChanged = currentEnable2FA !== originalEnable2FA;
        return switchesChanged || mainStateChanged;
    }, [isSms2faEnabled, isEmail2faEnabled, originalSms2faEnabled, originalEmail2faEnabled, originalEnable2FA]);

    useEffect(() => {
        getTwoFactorAuthenticationData();
    }, [getTwoFactorAuthenticationData]);


    const handleSave = async (manuallyCalled = false) => {
        // Prevent double API calls
        if (isLoading) return;
        
        try {
            setIsLoading(true);
            const formData = {
                "isEmail2faEnabled": manuallyCalled ? false : isEmail2faEnabled,
                "isSms2faEnabled": manuallyCalled ? false : isSms2faEnabled
            };
            await UserProfileAPI.updateTwoFactorStatus(formData, companyId, token)
                .then((response) => {                    
                    const newEnable2FA = isSms2faEnabled || isEmail2faEnabled;
                    setOriginalSms2faEnabled(isSms2faEnabled);
                    setOriginalEmail2faEnabled(isEmail2faEnabled);
                    setOriginalEnable2FA(newEnable2FA);
                    // Only check if both are false for regular saves (not manual disable)
                    if (!manuallyCalled && !isEmail2faEnabled && !isSms2faEnabled) {
                        setEnable2FA(false);
                    }
                    openNotificationWithIcon("success", "Successful", `${response.data.message}`, "bottomLeft");
                })
                .catch((err) => {
                    if (manuallyCalled) {
                        // Revert state on error for manual disable
                        setEnable2FA(true);
                        setIsSms2faEnabled(originalSms2faEnabled); 
                        setIsEmail2faEnabled(originalEmail2faEnabled);
                        openNotificationWithIcon("error", "Two-Factor authentication disable failed", "Failed to disable 2FA. Please try again.", "bottomLeft");
                    } else {
                        if (err.response) {
                            openNotificationWithIcon("error", "Two-Factor authentication update failed", `${err.response.data.message}`, "bottomLeft");
                        } else {
                            openNotificationWithIcon("error", "Two-Factor authentication update failed", `${err.message}`, "bottomLeft");
                        }
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } catch (error) {
            console.log('Error in authentication save :', error);
            setIsLoading(false);
        }
    };

    const handleCancel = () => {        
        setIsSms2faEnabled(originalSms2faEnabled);
        setIsEmail2faEnabled(originalEmail2faEnabled);
        setEnable2FA(originalEnable2FA);
    };

    return (
        <div
            style={{
                margin: '16px',
                padding: '16px 24px',
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}
        >
            <div
                style={{
                    width: '100%',
                }}
            >
                {
                    is2FALoading ? (
                        <CommonSkeleton itemCount={2} />
                    ) : (
                        <Flex vertical gap={24}>
                            <Flex justify="space-between" align="flex-start">
                                <Flex vertical>
                                    <Text strong>Two-Factor Authentication</Text>
                                    <Text type="secondary">
                                        Enhance your account security with two-factor authentication (2FA). Enable to choose verification methods.
                                    </Text>
                                </Flex>
                                <Switch
                                    checked={enable2FA}
                                    loading={isLoading}
                                    onChange={(checked) => {
                                        setEnable2FA(checked);
                                        if (!checked) {
                                            setIsSms2faEnabled(false);
                                            setIsEmail2faEnabled(false);
                                        }
                                    }}
                                    style={{ backgroundColor: enable2FA ? colorPrimary : undefined }}
                                />
                            </Flex>

                            {(enable2FA || hasChanges) && (
                                <>
                                    <hr style={{ border: `1px solid ${colorBorder}`, margin: '8px 0' }} />
                                    <Form layout="vertical" autoComplete="off">
                                        <Row gutter={16}>
                                            <Col xs={24}>
                                                <Flex justify="space-between" align="center">
                                                    <Flex vertical>
                                                        <span strong>Enable SMS Authentication</span>
                                                        <Text type='secondary'>Receive a secure code via SMS to <b>{maskedMobile}</b></Text>
                                                    </Flex>
                                                    <Switch
                                                        disabled={isDisabledSMS}
                                                        checked={isSms2faEnabled}
                                                        onChange={(checked) => {
                                                            setIsSms2faEnabled(checked);
                                                        }}
                                                        style={{ backgroundColor: isSms2faEnabled ? colorPrimary : undefined }}
                                                    />
                                                </Flex>
                                            </Col>
                                        </Row>
                                        <hr style={{ border: `1px solid ${colorBorder}`, margin: '24px 0' }} />
                                        <Row gutter={16}>
                                            <Col xs={24}>
                                                <Flex justify="space-between" align="center">
                                                    <Flex vertical>
                                                        <span strong>Enable Email Authentication</span>
                                                        <Text type='secondary'>Receive a secure code via Email to <b>{maskedEmail}</b></Text>
                                                    </Flex>
                                                    <Switch
                                                        checked={isEmail2faEnabled}
                                                        onChange={(checked) => {
                                                            setIsEmail2faEnabled(checked);
                                                        }}
                                                        style={{ backgroundColor: isEmail2faEnabled ? colorPrimary : undefined }}
                                                    />
                                                </Flex>
                                            </Col>
                                        </Row>
                                    </Form>
                                </>
                            )}

                            <Flex justify="flex-end" gap={16}>
                                <Button
                                    type="default"
                                    onClick={handleCancel}
                                    style={{ borderRadius: borderRadiusLG }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    disabled={!hasChanges || isLoading}
                                    loading={isLoading}
                                    onClick={() => handleSave()}
                                    style={{ borderRadius: borderRadiusLG }}
                                >
                                    Save
                                </Button>
                            </Flex>
                        </Flex>
                    )
                }
            </div>
            {contextHolder}
        </div>
    );
}